import { useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api, dateOnly } from '../lib/api';

/**
 * Customer register: GOV.UK Frontend data table, form and tag components.
 * Read-only presentation replacement; data queries and account actions unchanged.
 */
export default function Customers() {
  const navigate = useNavigate();
  const [query,setQuery] = useState('');
  const [rows,setRows] = useState([]);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [refreshKey,setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api('/api/customers?pageSize=100')
      .then(data => { if(active) setRows(data.customers || []); })
      .catch(cause => { if(active) setError(cause.message); })
      .finally(() => { if(active) setLoading(false); });
    return () => { active = false; };
  }, [refreshKey]);

  const filtered = useMemo(() => rows.filter(row =>
    `${row.display_name || ''} ${row.customer_number || ''} ${row.verified_email || ''} ${row.contact_email || ''}`
      .toLowerCase().includes(query.toLowerCase())
  ), [rows,query]);
  const totals = useMemo(() => ({
    customers:rows.length,
    active:rows.filter(row => String(row.account_status || '').toLowerCase() === 'active').length,
    cases:rows.reduce((sum,row) => sum + Number(row.open_case_count || 0),0),
    services:rows.reduce((sum,row) => sum + Number(row.platform_count || 0),0),
  }), [rows]);

  const statusTone = value => {
    const status=String(value || '').toLowerCase();
    return status === 'active' ? 'govuk-tag--green'
      : status === 'suspended' || status === 'restricted' ? 'govuk-tag--red'
      : 'govuk-tag--grey';
  };

  return <div className="govuk-width-container" id="customer-register">
    <div className="govuk-grid-row">
      <div className="govuk-grid-column-two-thirds">
        <span className="govuk-caption-l">Customer Operations / Customer 360</span>
        <h1 className="govuk-heading-xl">Customer records</h1>
        <p className="govuk-body">Search customer accounts, review their services and open their existing Customer 360 records.</p>
      </div>
      <div className="govuk-grid-column-one-third">
        <div className="govuk-button-group">
          <button className="govuk-button govuk-button--secondary" type="button"
            onClick={() => setRefreshKey(value => value + 1)} disabled={loading}>
            <RefreshCw size={16} aria-hidden="true"/> Refresh records
          </button>
        </div>
      </div>
    </div>

    <section aria-label="Customer register summary" className="govuk-!-margin-bottom-7">
      <h2 className="govuk-heading-m">Register summary</h2>
      <dl className="govuk-summary-list">
        <div className="govuk-summary-list__row"><dt className="govuk-summary-list__key">Customer records</dt><dd className="govuk-summary-list__value">{totals.customers}</dd></div>
        <div className="govuk-summary-list__row"><dt className="govuk-summary-list__key">Active accounts</dt><dd className="govuk-summary-list__value">{totals.active}</dd></div>
        <div className="govuk-summary-list__row"><dt className="govuk-summary-list__key">Open cases</dt><dd className="govuk-summary-list__value">{totals.cases}</dd></div>
        <div className="govuk-summary-list__row"><dt className="govuk-summary-list__key">Service relationships</dt><dd className="govuk-summary-list__value">{totals.services}</dd></div>
      </dl>
    </section>

    <section aria-labelledby="customer-register-results">
      <h2 id="customer-register-results" className="govuk-heading-m">Find a customer</h2>
      <div className="govuk-form-group">
        <label className="govuk-label govuk-label--m" htmlFor="customer-register-search">Search customer records</label>
        <div id="customer-register-search-hint" className="govuk-hint">Enter a name, email address or customer number.</div>
        <input id="customer-register-search" className="govuk-input govuk-!-width-two-thirds" type="search"
          value={query} onChange={event => setQuery(event.target.value)}
          aria-describedby="customer-register-search-hint"/>
      </div>

      {error ? <div className="govuk-error-summary" role="alert">
        <h2 className="govuk-error-summary__title govuk-heading-m">Customer records unavailable</h2>
        <div className="govuk-error-summary__body"><p className="govuk-body">{error}</p></div>
      </div> : null}

      {loading ? <p className="govuk-body" role="status">Loading customer records…</p>
        : !error && <>
          <p className="govuk-body-s" aria-live="polite">Showing {filtered.length} of {rows.length} customer records.</p>
          {filtered.length ? <div className="govuk-table__container" role="region" aria-label="Customer records" tabIndex={0}>
            <table className="govuk-table">
              <caption className="govuk-table__caption govuk-table__caption--m">Customer register</caption>
              <thead className="govuk-table__head"><tr className="govuk-table__row">
                <th scope="col" className="govuk-table__header">Customer</th>
                <th scope="col" className="govuk-table__header">Customer number</th>
                <th scope="col" className="govuk-table__header">Account status</th>
                <th scope="col" className="govuk-table__header">Open cases</th>
                <th scope="col" className="govuk-table__header">Services</th>
                <th scope="col" className="govuk-table__header">Last updated</th>
                <th scope="col" className="govuk-table__header">Action</th>
              </tr></thead>
              <tbody className="govuk-table__body">
                {filtered.map(row => <tr className="govuk-table__row" key={row.id}>
                  <td className="govuk-table__cell">
                    <strong>{row.display_name || row.verified_email || 'Unnamed customer'}</strong>
                    <span className="govuk-hint govuk-!-margin-bottom-0">{row.verified_email || row.contact_email || 'No email recorded'}</span>
                  </td>
                  <td className="govuk-table__cell">{row.customer_number || '—'}</td>
                  <td className="govuk-table__cell"><strong className={`govuk-tag ${statusTone(row.account_status)}`}>{String(row.account_status || 'unknown').replaceAll('_',' ')}</strong></td>
                  <td className="govuk-table__cell">{Number(row.open_case_count || 0)}</td>
                  <td className="govuk-table__cell">{Number(row.platform_count || 0)}</td>
                  <td className="govuk-table__cell">{dateOnly(row.updated_at || row.last_activity_at)}</td>
                  <td className="govuk-table__cell"><button type="button" className="govuk-link govuk-link--no-visited-state"
                    onClick={() => navigate('/customers/' + encodeURIComponent(row.id))}
                    aria-label={`Open Customer 360 record for ${row.display_name || row.customer_number || 'customer'}`}>Open record</button></td>
                </tr>)}
              </tbody>
            </table>
          </div> : <div className="govuk-inset-text">No customer records match your search.</div>}
        </>}
    </section>
  </div>;
}
